/**
 * ImpostaSoggiornoPage — carica file Booking (Excel, CSV, screenshot)
 * e visualizza adulti × notti / bambini × notti per mese per la
 * rendicontazione della tassa di soggiorno al Comune.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UploadSimple, FileXls, FileCsv, Image as ImageIcon, Trash, CheckCircle, Info, Download, ArrowClockwise, AirplaneTilt, Minus, Plus as PlusIcon, FloppyDisk, Users, Baby, Buildings, FilePdf, X as XIcon } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ComuniManager from '../components/ComuniManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const monthNameIT = (key) => {
  try {
    const [y, m] = key.split('-').map(Number);
    const months = ['gennaio','febbraio','marzo','aprile','maggio','giugno','luglio','agosto','settembre','ottobre','novembre','dicembre'];
    return `${months[m-1]} ${y}`;
  } catch { return key; }
};

export default function ImpostaSoggiornoPage() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [current, setCurrent] = useState(null);  // active report
  const [history, setHistory] = useState([]);    // previous reports
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [editDraft, setEditDraft] = useState([]); // draft reservations (Airbnb review mode)
  const [savingReview, setSavingReview] = useState(false);
  const [bulkAdults, setBulkAdults] = useState(2);
  const [bulkChildren, setBulkChildren] = useState(0);
  const [comuni, setComuni] = useState([]);
  const [applyingComune, setApplyingComune] = useState(false);
  const [downloadingMonth, setDownloadingMonth] = useState(null);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualDraft, setManualDraft] = useState({
    check_in: '',
    check_out: '',
    adults: 2,
    children: 0,
    children_exempt: 0,
    guest_name: '',
  });
  const [savingManual, setSavingManual] = useState(false);
  const [annualSummary, setAnnualSummary] = useState(null);

  const fmtEur = (v) => {
    const n = Number(v) || 0;
    return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/imposta-soggiorno/reports`, { withCredentials: true });
      setHistory(r.data.items || []);
    } catch {
      // ignore
    } finally {
      setLoadingHistory(false);
    }
    // Refresh annual combined summary in parallel
    try {
      const s = await axios.get(`${API}/imposta-soggiorno/summary`, { withCredentials: true });
      setAnnualSummary(s.data);
    } catch {
      setAnnualSummary(null);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File troppo grande (max 10MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/imposta-soggiorno/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCurrent(r.data);
      if (r.data.needs_review) {
        setEditDraft(r.data.reservations || []);
        toast.info(`${r.data.reservations_count} prenotazioni Airbnb caricate · compila adulti e bambini`, { duration: 5000 });
      } else {
        setEditDraft([]);
        toast.success(`${r.data.reservations_count} prenotazioni elaborate`);
      }
      await fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const onFileSelected = (e) => {
    const f = e.target.files?.[0];
    if (f) handleUpload(f);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleUpload(f);
  };

  const loadReport = async (id) => {
    try {
      const r = await axios.get(`${API}/imposta-soggiorno/reports/${id}`, { withCredentials: true });
      setCurrent(r.data);
      setEditDraft(r.data.needs_review ? (r.data.reservations || []) : []);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Errore caricamento report');
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Eliminare questo report?')) return;
    try {
      await axios.delete(`${API}/imposta-soggiorno/reports/${id}`, { withCredentials: true });
      toast.success('Report eliminato');
      if (current?.id === id) setCurrent(null);
      await fetchHistory();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const setRowField = (idx, field, value) => {
    setEditDraft(prev => prev.map((r, i) => i === idx ? { ...r, [field]: Math.max(0, Number(value) || 0) } : r));
  };
  const incRow = (idx, field, delta) => {
    setEditDraft(prev => prev.map((r, i) => i === idx ? { ...r, [field]: Math.max(0, (Number(r[field]) || 0) + delta) } : r));
  };
  const applyBulk = () => {
    setEditDraft(prev => prev.map(r => ({ ...r, adults: bulkAdults, children: bulkChildren })));
    toast.success(`Applicato ${bulkAdults} adulti + ${bulkChildren} bambini a tutte`);
  };
  const saveReview = async () => {
    if (!current?.id) return;
    const totalFilled = editDraft.reduce((s, r) => s + (r.adults || 0) + (r.children || 0), 0);
    if (totalFilled === 0) {
      toast.error('Inserisci almeno un ospite per continuare');
      return;
    }
    setSavingReview(true);
    try {
      const r = await axios.put(`${API}/imposta-soggiorno/reports/${current.id}`, { reservations: editDraft }, { withCredentials: true });
      setCurrent(r.data);
      setEditDraft(r.data.needs_review ? (r.data.reservations || []) : []);
      toast.success('Report aggiornato · aggregato mensile calcolato');
      await fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSavingReview(false);
    }
  };

  const applyComune = async (comuneId) => {
    if (!current?.id) return;
    setApplyingComune(true);
    try {
      const r = await axios.post(`${API}/imposta-soggiorno/reports/${current.id}/apply-comune`, { comune_id: comuneId || null }, { withCredentials: true });
      setCurrent(r.data);
      if (comuneId) {
        toast.success(`Calcolo aggiornato per ${r.data.comune_snapshot?.name}`);
      } else {
        toast.info('Regole Comune rimosse');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore applicazione Comune');
    } finally {
      setApplyingComune(false);
    }
  };

  const submitManualEntry = async () => {
    if (!manualDraft.check_in || !manualDraft.check_out) {
      toast.error('Inserisci check-in e check-out');
      return;
    }
    if (manualDraft.check_in >= manualDraft.check_out) {
      toast.error('Il check-out deve essere successivo al check-in');
      return;
    }
    const totGuests = (Number(manualDraft.adults) || 0) + (Number(manualDraft.children) || 0);
    if (totGuests === 0) {
      toast.error('Inserisci almeno un ospite');
      return;
    }
    setSavingManual(true);
    try {
      const r = await axios.post(`${API}/imposta-soggiorno/manual-entry`, {
        check_in: manualDraft.check_in,
        check_out: manualDraft.check_out,
        adults: Number(manualDraft.adults) || 0,
        children: Number(manualDraft.children) || 0,
        children_exempt: Number(manualDraft.children_exempt) || 0,
        guest_name: manualDraft.guest_name || '',
        comune_id: current?.source_type === 'manual' ? null : (comuni[0]?.id || null),
      }, { withCredentials: true });
      setCurrent(r.data);
      setEditDraft([]);
      toast.success('Inserimento aggiunto');
      // Reset draft (keep guest_name empty)
      setManualDraft({
        check_in: '',
        check_out: '',
        adults: 2,
        children: 0,
        children_exempt: 0,
        guest_name: '',
      });
      setShowManualDialog(false);
      await fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSavingManual(false);
    }
  };

  const deleteManualEntry = async (index) => {
    if (!window.confirm('Eliminare questo inserimento manuale?')) return;
    try {
      const r = await axios.delete(`${API}/imposta-soggiorno/manual-entry/${index}`, { withCredentials: true });
      if (r.data?.deleted) {
        setCurrent(null);
        toast.success('Ultimo inserimento rimosso');
      } else {
        setCurrent(r.data);
        toast.success('Inserimento rimosso');
      }
      await fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const downloadMonthPdf = async (monthKey) => {
    if (!current?.id) return;
    if (!current.comune_snapshot) {
      toast.error('Applica prima un Comune al report');
      return;
    }
    setDownloadingMonth(monthKey);
    try {
      const r = await axios.get(`${API}/imposta-soggiorno/reports/${current.id}/pdf?month=${monthKey}`, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = current.comune_snapshot.name.replace(/[^A-Za-z0-9]+/g, '_');
      a.download = `imposta_soggiorno_${safe}_${monthKey}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch (e) {
      toast.error('Errore download PDF');
    } finally {
      setDownloadingMonth(null);
    }
  };

  const exportCSV = () => {
    if (!current) return;
    const hasComune = !!current.comune_snapshot;
    let rows;
    if (hasComune) {
      rows = [['Mese', 'Notti adulti', 'Notti minori esenti', 'Notti minori paganti', 'Notti tassabili', 'Imposta dovuta (€)', 'Prenotazioni']];
      Object.entries(current.monthly || {}).forEach(([k, v]) => {
        rows.push([monthNameIT(k), v.adult_nights, v.exempt_minors_nights || 0, v.taxable_minors_nights || 0, v.taxable_nights || 0, (v.tax_due || 0).toFixed(2).replace('.', ','), v.reservations_count]);
      });
    } else {
      rows = [['Mese', 'Adulti × Notti', 'Bambini × Notti', 'Notti totali', 'Prenotazioni']];
      Object.entries(current.monthly || {}).forEach(([k, v]) => {
        rows.push([monthNameIT(k), v.adult_nights, v.children_nights || 0, v.total_nights, v.reservations_count]);
      });
    }
    const csv = rows.map(r => r.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `imposta_soggiorno_${current.filename.replace(/\.[^.]+$/, '')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="imposta-soggiorno-page">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-zinc-900" data-testid="back-btn">
            <ArrowLeft size={14} weight="bold" /> Dashboard
          </button>
          <h1 className="text-sm sm:text-base font-black tracking-tight text-zinc-900">Imposta di Soggiorno</h1>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Intro */}
        <div className="mb-6 sm:mb-8">
          <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-blue-600 mb-1.5">Rendicontazione</p>
          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-zinc-900 mb-2">Calcola adulti e bambini per mese</h2>
          <p className="text-sm text-zinc-500 leading-relaxed max-w-2xl">
            Carica il file prenotazioni di Booking / Airbnb (Excel, CSV) o anche uno screenshot — il sistema estrae automaticamente il totale di adulti × notti e bambini × notti per ogni mese. Configura i Comuni in cui hai strutture per ottenere il calcolo automatico dell'imposta dovuta.
          </p>
        </div>

        {/* Comuni manager */}
        <ComuniManager onChange={setComuni} />

        {/* ⚠️ Booking disclaimer with screenshot */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 sm:p-5 mb-4" data-testid="booking-disclaimer">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Info size={18} weight="bold" className="text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-amber-900 mb-1.5">
                📎 Da Booking scarica il file dalla sezione <strong>“Prenotazioni”</strong>, NON da <strong>“Contabilità”</strong>
              </p>
              <p className="text-xs text-amber-800 leading-relaxed">
                Il file di Booking corretto è quello degli <strong>arrivi</strong> (esempio: <code className="bg-white/60 px-1 py-0.5 rounded font-mono text-[10px]">Arrivo_YYYY-MM-DD.xls</code>): contiene le colonne <strong>Adulti</strong>, <strong>Bambini</strong> e <strong>Età dei bambini</strong> necessarie per il calcolo automatico dell'imposta.
                Il file dalla sezione “Contabilità” (<code className="bg-white/60 px-1 py-0.5 rounded font-mono text-[10px]">reservation_statements_overview.csv</code>) contiene solo il totale “Persons” e richiede lo splitting manuale.
              </p>
            </div>
          </div>
          <a
            href="https://customer-assets-lxgj4vgw.emergentagent.net/job_rental-sign-2/artifacts/ng1es6yz_Screenshot%202026-09-02%20alle%2018.43.48.png"
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-xl overflow-hidden border border-amber-200 bg-white hover:opacity-95 transition-opacity"
            data-testid="booking-disclaimer-screenshot"
          >
            <img
              src="https://customer-assets-lxgj4vgw.emergentagent.net/job_rental-sign-2/artifacts/ng1es6yz_Screenshot%202026-09-02%20alle%2018.43.48.png"
              alt="Sezione Prenotazioni di Booking Extranet — Data di: Arrivo, filtro Da/Fino a, pulsante Scarica"
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </a>
          <p className="text-[10px] text-amber-700 mt-2 italic text-center">
            Vai su Booking Extranet → <strong>Prenotazioni</strong> → imposta <em>Data di: Arrivo</em>, seleziona il periodo, poi clicca <strong>Scarica</strong>.
          </p>
        </div>

        {/* Upload area */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`bg-white border-2 border-dashed ${dragging ? 'border-blue-500 bg-blue-50' : 'border-zinc-300'} hover:border-blue-400 transition-colors cursor-pointer p-8 sm:p-12 text-center mb-6`}
          data-testid="upload-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv,image/*"
            className="hidden"
            onChange={onFileSelected}
            data-testid="file-input"
          />
          <div className="w-14 h-14 bg-blue-100 text-blue-700 flex items-center justify-center mx-auto mb-4">
            {uploading ? <ArrowClockwise size={24} weight="bold" className="animate-spin" /> : <UploadSimple size={24} weight="bold" />}
          </div>
          <p className="text-sm font-black text-zinc-900 mb-1 tracking-tight">
            {uploading ? 'Elaborazione in corso...' : 'Trascina qui il file o clicca per caricare'}
          </p>
          <p className="text-xs text-zinc-500 mb-3">Excel (.xlsx) · CSV · Immagine (PNG/JPG)</p>
          <div className="flex items-center justify-center gap-3 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
            <span className="inline-flex items-center gap-1"><FileXls size={14} /> Excel</span>
            <span className="inline-flex items-center gap-1"><FileCsv size={14} /> CSV</span>
            <span className="inline-flex items-center gap-1"><ImageIcon size={14} /> Screenshot</span>
          </div>
        </div>

        {/* Annual combined summary — manuale + Excel + CSV insieme */}
        {annualSummary && (annualSummary.sources_count || 0) > 0 && (
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-500 p-4 sm:p-5 mb-6" data-testid="annual-summary-card">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 mb-1">Resoconto annuale combinato</p>
                <h2 className="text-lg sm:text-xl font-black tracking-tight text-zinc-900">
                  {fmtEur(annualSummary.total_tax_due)} <span className="text-zinc-400 text-sm font-bold">· {annualSummary.total_reservations} prenotazioni</span>
                </h2>
                <p className="text-[11px] text-zinc-600 mt-1">
                  Somma di <strong>{annualSummary.sources_count}</strong> {annualSummary.sources_count === 1 ? 'fonte' : 'fonti'} (Excel · CSV · Inserimenti manuali). Usalo per la rendicontazione annua complessiva.
                </p>
              </div>
              {annualSummary.comune_snapshot && (
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-white border border-emerald-300 text-emerald-700">
                  {annualSummary.comune_snapshot.name} · €{Number(annualSummary.comune_snapshot.tax_per_night).toFixed(2)}/nt
                </span>
              )}
            </div>
            <details className="mt-3" data-testid="annual-summary-details">
              <summary className="cursor-pointer text-[11px] font-black uppercase tracking-wider text-emerald-700 hover:text-emerald-900 select-none inline-flex items-center gap-1">
                Dettaglio mese per mese ›
              </summary>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm border-t border-emerald-200">
                  <thead>
                    <tr className="bg-white/60">
                      <th className="px-3 py-2 text-left text-[10px] font-black uppercase tracking-wider text-zinc-600">Mese</th>
                      <th className="px-3 py-2 text-right text-[10px] font-black uppercase tracking-wider text-blue-700">Adulti × Notti</th>
                      <th className="px-3 py-2 text-right text-[10px] font-black uppercase tracking-wider text-amber-700 hidden sm:table-cell">Min. paganti × Notti</th>
                      <th className="px-3 py-2 text-right text-[10px] font-black uppercase tracking-wider text-zinc-900">Imposta</th>
                      <th className="px-3 py-2 text-left text-[10px] font-black uppercase tracking-wider text-zinc-500 hidden md:table-cell">Fonti</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(annualSummary.monthly || {}).map(([key, v]) => (
                      <tr key={key} className="border-b border-emerald-100">
                        <td className="px-3 py-2 font-bold text-zinc-900 capitalize">{monthNameIT(key)}</td>
                        <td className="px-3 py-2 text-right font-black text-blue-700 tabular-nums">{v.adult_nights}</td>
                        <td className="px-3 py-2 text-right text-amber-700 tabular-nums hidden sm:table-cell">{v.taxable_minors_nights || 0}</td>
                        <td className="px-3 py-2 text-right font-black text-zinc-900 tabular-nums">{fmtEur(v.tax_due)}</td>
                        <td className="px-3 py-2 text-[10px] text-zinc-500 hidden md:table-cell truncate max-w-[180px]" title={(v.sources || []).join(', ')}>{(v.sources || []).join(' · ')}</td>
                      </tr>
                    ))}
                    <tr className="bg-emerald-100 border-t-2 border-emerald-600">
                      <td className="px-3 py-2 font-black text-zinc-900 uppercase text-xs tracking-wider">Totale</td>
                      <td className="px-3 py-2 text-right font-black text-blue-700 tabular-nums">
                        {Object.values(annualSummary.monthly || {}).reduce((s, v) => s + (v.adult_nights || 0), 0)}
                      </td>
                      <td className="px-3 py-2 text-right text-amber-700 tabular-nums hidden sm:table-cell">
                        {Object.values(annualSummary.monthly || {}).reduce((s, v) => s + (v.taxable_minors_nights || 0), 0)}
                      </td>
                      <td className="px-3 py-2 text-right font-black text-zinc-900 tabular-nums">{fmtEur(annualSummary.total_tax_due)}</td>
                      <td className="hidden md:table-cell" />
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        )}

        {/* Manual entry CTA */}
        <div className="bg-white border border-zinc-200 p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="manual-entry-cta">
          <div className="flex items-start sm:items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0">
              <PlusIcon size={18} weight="bold" />
            </div>
            <div>
              <p className="text-sm font-black text-zinc-900 tracking-tight">Oppure aggiungi una prenotazione manualmente</p>
              <p className="text-[11px] text-zinc-500 leading-snug mt-0.5">Inserisci date, adulti e minori esenti per età. Il calcolo è automatico.</p>
            </div>
          </div>
          <Button
            onClick={() => setShowManualDialog(true)}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs uppercase tracking-wider px-4 py-2.5 flex-shrink-0"
            data-testid="open-manual-dialog-btn"
          >
            <PlusIcon size={14} weight="bold" className="mr-1.5" />
            Aggiungi manualmente
          </Button>
        </div>

        {/* Manual entry dialog */}
        {showManualDialog && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="manual-entry-dialog">
            <div className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                <h3 className="text-sm font-black text-zinc-900 tracking-tight uppercase">Nuova prenotazione manuale</h3>
                <button onClick={() => setShowManualDialog(false)} className="p-1 hover:bg-zinc-100" data-testid="close-manual-dialog-btn">
                  <XIcon size={18} weight="bold" className="text-zinc-500" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Check-in</label>
                    <input
                      type="date"
                      value={manualDraft.check_in}
                      onChange={e => setManualDraft({ ...manualDraft, check_in: e.target.value })}
                      className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                      data-testid="manual-check-in-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Check-out</label>
                    <input
                      type="date"
                      value={manualDraft.check_out}
                      onChange={e => setManualDraft({ ...manualDraft, check_out: e.target.value })}
                      className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                      data-testid="manual-check-out-input"
                    />
                  </div>
                </div>
                {manualDraft.check_in && manualDraft.check_out && manualDraft.check_in < manualDraft.check_out && (
                  <p className="text-[11px] text-blue-700 font-medium">
                    {(() => { const d = (new Date(manualDraft.check_out) - new Date(manualDraft.check_in)) / 86400000; return `${d} nott${d === 1 ? 'e' : 'i'}`; })()}
                  </p>
                )}

                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Adulti</label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setManualDraft({ ...manualDraft, adults: Math.max(0, manualDraft.adults - 1) })} className="w-8 h-8 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="manual-adults-dec"><Minus size={12} weight="bold" /></button>
                    <input
                      type="number"
                      min="0"
                      value={manualDraft.adults}
                      onChange={e => setManualDraft({ ...manualDraft, adults: Math.max(0, Number(e.target.value) || 0) })}
                      className="w-16 h-8 text-center border border-zinc-300 text-sm font-black tabular-nums"
                      data-testid="manual-adults-input"
                    />
                    <button type="button" onClick={() => setManualDraft({ ...manualDraft, adults: manualDraft.adults + 1 })} className="w-8 h-8 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="manual-adults-inc"><PlusIcon size={12} weight="bold" /></button>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Minori (totale)</label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setManualDraft({ ...manualDraft, children: Math.max(0, manualDraft.children - 1) })} className="w-8 h-8 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="manual-children-dec"><Minus size={12} weight="bold" /></button>
                    <input
                      type="number"
                      min="0"
                      value={manualDraft.children}
                      onChange={e => setManualDraft({ ...manualDraft, children: Math.max(0, Number(e.target.value) || 0) })}
                      className="w-16 h-8 text-center border border-zinc-300 text-sm font-black tabular-nums"
                      data-testid="manual-children-input"
                    />
                    <button type="button" onClick={() => setManualDraft({ ...manualDraft, children: manualDraft.children + 1 })} className="w-8 h-8 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="manual-children-inc"><PlusIcon size={12} weight="bold" /></button>
                  </div>
                </div>

                {manualDraft.children > 0 && (
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Minori esenti per età</label>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setManualDraft({ ...manualDraft, children_exempt: Math.max(0, manualDraft.children_exempt - 1) })} className="w-8 h-8 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="manual-exempt-dec"><Minus size={12} weight="bold" /></button>
                      <input
                        type="number"
                        min="0"
                        max={manualDraft.children}
                        value={manualDraft.children_exempt}
                        onChange={e => setManualDraft({ ...manualDraft, children_exempt: Math.min(manualDraft.children, Math.max(0, Number(e.target.value) || 0)) })}
                        className="w-16 h-8 text-center border border-zinc-300 text-sm font-black tabular-nums"
                        data-testid="manual-exempt-input"
                      />
                      <button type="button" onClick={() => setManualDraft({ ...manualDraft, children_exempt: Math.min(manualDraft.children, manualDraft.children_exempt + 1) })} className="w-8 h-8 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="manual-exempt-inc"><PlusIcon size={12} weight="bold" /></button>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">su {manualDraft.children}</span>
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-1">Esempio: minore di 12 anni se il Comune esenta fino a 12</p>
                  </div>
                )}

                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Nome ospite <span className="font-normal normal-case text-zinc-400">(opzionale)</span></label>
                  <input
                    type="text"
                    placeholder="Mario Rossi"
                    value={manualDraft.guest_name}
                    onChange={e => setManualDraft({ ...manualDraft, guest_name: e.target.value })}
                    className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                    data-testid="manual-guest-name-input"
                  />
                </div>
              </div>
              <div className="px-5 py-4 border-t border-zinc-200 flex justify-end gap-2">
                <Button onClick={() => setShowManualDialog(false)} variant="outline" className="rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider" data-testid="cancel-manual-btn">
                  Annulla
                </Button>
                <Button onClick={submitManualEntry} disabled={savingManual} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider" data-testid="save-manual-btn">
                  {savingManual ? 'Salvataggio…' : 'Aggiungi'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Airbnb review table — shown when the upload has reservations without adult/children data */}
        {current && current.needs_review && (
          <div className="bg-white border border-pink-200 p-5 sm:p-6 mb-6" data-testid="airbnb-review">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 text-white flex items-center justify-center flex-shrink-0">
                <AirplaneTilt size={20} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-1.5 bg-pink-50 text-pink-700 border border-pink-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider mb-1.5">
                  File Airbnb · {current.reservations_count} prenotazioni
                </div>
                <p className="text-sm font-black text-zinc-900 tracking-tight mb-1">Compila adulti e bambini per ogni prenotazione</p>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Airbnb non include il numero di ospiti nel file esportato. Inserisci i dati qui sotto e salva per generare l'aggregato mensile.
                </p>
              </div>
            </div>

            {/* Bulk apply */}
            <div className="bg-zinc-50 border border-zinc-200 p-3 sm:p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600 flex-shrink-0">Applica a tutte</p>
              <div className="flex items-center gap-2">
                <Users size={14} weight="bold" className="text-blue-700" />
                <button onClick={() => setBulkAdults(Math.max(0, bulkAdults - 1))} className="w-7 h-7 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="bulk-adults-dec"><Minus size={10} weight="bold" /></button>
                <input type="number" min="0" value={bulkAdults} onChange={e => setBulkAdults(Math.max(0, Number(e.target.value) || 0))} className="w-12 h-7 text-center border border-zinc-300 text-sm font-black tabular-nums" data-testid="bulk-adults-input" />
                <button onClick={() => setBulkAdults(bulkAdults + 1)} className="w-7 h-7 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="bulk-adults-inc"><PlusIcon size={10} weight="bold" /></button>
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">adulti</span>
              </div>
              <div className="flex items-center gap-2">
                <Baby size={14} weight="bold" className="text-amber-700" />
                <button onClick={() => setBulkChildren(Math.max(0, bulkChildren - 1))} className="w-7 h-7 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="bulk-children-dec"><Minus size={10} weight="bold" /></button>
                <input type="number" min="0" value={bulkChildren} onChange={e => setBulkChildren(Math.max(0, Number(e.target.value) || 0))} className="w-12 h-7 text-center border border-zinc-300 text-sm font-black tabular-nums" data-testid="bulk-children-input" />
                <button onClick={() => setBulkChildren(bulkChildren + 1)} className="w-7 h-7 border border-zinc-300 hover:border-zinc-900 flex items-center justify-center" data-testid="bulk-children-inc"><PlusIcon size={10} weight="bold" /></button>
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">bambini</span>
              </div>
              <Button onClick={applyBulk} variant="outline" className="rounded-none border-zinc-900 text-zinc-900 text-xs font-bold uppercase tracking-wider h-8 ml-auto" data-testid="bulk-apply-btn">
                Applica a tutte
              </Button>
            </div>

            {/* Editable rows */}
            <div className="overflow-x-auto -mx-5 sm:mx-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-zinc-200 bg-zinc-50">
                    <th className="px-3 py-2 text-left text-[10px] font-black uppercase tracking-wider text-zinc-600">Ospite</th>
                    <th className="px-3 py-2 text-left text-[10px] font-black uppercase tracking-wider text-zinc-600">Check-in → Check-out</th>
                    <th className="px-3 py-2 text-center text-[10px] font-black uppercase tracking-wider text-zinc-600 hidden sm:table-cell">Notti</th>
                    <th className="px-3 py-2 text-center text-[10px] font-black uppercase tracking-wider text-blue-700">Adulti</th>
                    <th className="px-3 py-2 text-center text-[10px] font-black uppercase tracking-wider text-amber-700">Bambini</th>
                  </tr>
                </thead>
                <tbody>
                  {editDraft.map((r, i) => (
                    <tr key={r.code || i} className="border-b border-zinc-100" data-testid={`review-row-${i}`}>
                      <td className="px-3 py-2.5">
                        <p className="font-bold text-zinc-900 truncate max-w-[180px]">{r.guest_name || '—'}</p>
                        {r.code && <p className="text-[10px] text-zinc-400 font-mono">{r.code}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-zinc-600 tabular-nums whitespace-nowrap">
                        {new Date(r.check_in).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} → {new Date(r.check_out).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                      </td>
                      <td className="px-3 py-2.5 text-center text-xs text-zinc-500 tabular-nums hidden sm:table-cell">{r.nights}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => incRow(i, 'adults', -1)} className="w-6 h-6 border border-zinc-300 hover:border-blue-700 flex items-center justify-center" data-testid={`row-${i}-adults-dec`}><Minus size={9} weight="bold" /></button>
                          <input type="number" min="0" value={r.adults || 0} onChange={e => setRowField(i, 'adults', e.target.value)} className="w-10 h-6 text-center border border-zinc-300 text-xs font-black tabular-nums text-blue-700" data-testid={`row-${i}-adults-input`} />
                          <button onClick={() => incRow(i, 'adults', 1)} className="w-6 h-6 border border-zinc-300 hover:border-blue-700 flex items-center justify-center" data-testid={`row-${i}-adults-inc`}><PlusIcon size={9} weight="bold" /></button>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => incRow(i, 'children', -1)} className="w-6 h-6 border border-zinc-300 hover:border-amber-700 flex items-center justify-center" data-testid={`row-${i}-children-dec`}><Minus size={9} weight="bold" /></button>
                          <input type="number" min="0" value={r.children || 0} onChange={e => setRowField(i, 'children', e.target.value)} className="w-10 h-6 text-center border border-zinc-300 text-xs font-black tabular-nums text-amber-700" data-testid={`row-${i}-children-input`} />
                          <button onClick={() => incRow(i, 'children', 1)} className="w-6 h-6 border border-zinc-300 hover:border-amber-700 flex items-center justify-center" data-testid={`row-${i}-children-inc`}><PlusIcon size={9} weight="bold" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <p className="text-xs text-zinc-500">
                <span className="font-bold text-zinc-900">Suggerimento:</span> usa "Applica a tutte" per impostare rapidamente il caso più comune, poi correggi singolarmente le eccezioni (famiglie, gruppi con bambini).
              </p>
              <Button onClick={saveReview} disabled={savingReview} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider h-10 px-5 flex-shrink-0" data-testid="save-review-btn">
                {savingReview ? <ArrowClockwise size={14} weight="bold" className="animate-spin mr-2" /> : <FloppyDisk size={14} weight="bold" className="mr-2" />}
                Salva e calcola
              </Button>
            </div>
          </div>
        )}

        {/* Current result */}
        {current && !current.needs_review && (
          <div className="bg-white border border-zinc-200 p-5 sm:p-6 mb-6" data-testid="current-report">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider mb-2">
                  <CheckCircle size={11} weight="fill" /> Elaborato
                </div>
                <p className="text-sm font-black text-zinc-900 tracking-tight break-all">{current.filename}</p>
                <p className="text-xs text-zinc-500">{current.reservations_count} prenotazioni valide · formato {current.source_type}</p>
              </div>
              <Button onClick={exportCSV} variant="outline" className="rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider flex-shrink-0" data-testid="export-csv-btn">
                <Download size={13} weight="bold" className="mr-1" /> CSV
              </Button>
            </div>

            {/* Comune selector */}
            <div className="bg-zinc-50 border border-zinc-200 p-3 sm:p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="comune-selector">
              <div className="flex items-center gap-2 flex-shrink-0">
                <Buildings size={16} weight="bold" className="text-blue-700" />
                <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600">Calcola tassa per</p>
              </div>
              {comuni.length === 0 ? (
                <p className="text-xs text-zinc-500 italic">Aggiungi un Comune nella sezione "Configurazioni Comuni" qui sopra ↑</p>
              ) : (
                <>
                  <select
                    value={current.comune_id || ''}
                    onChange={(e) => applyComune(e.target.value)}
                    disabled={applyingComune}
                    className="flex-1 h-9 px-3 border border-zinc-300 bg-white text-sm focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none"
                    data-testid="comune-select"
                  >
                    <option value="">— Nessuna regola Comune (solo notti) —</option>
                    {comuni.map(c => (
                      <option key={c.id} value={c.id}>{c.name} · €{c.tax_per_night.toFixed(2)}/notte · max {c.max_nights || '∞'} · esenti &lt;{c.exemption_age >= 18 ? '18 (tutti)' : c.exemption_age}</option>
                    ))}
                  </select>
                  {current.comune_snapshot && (
                    <div className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider flex-shrink-0">
                      <CheckCircle size={11} weight="fill" /> {current.comune_snapshot.name}
                      <button onClick={() => applyComune(null)} className="ml-1 hover:text-red-600" title="Rimuovi" data-testid="comune-detach-btn"><XIcon size={10} weight="bold" /></button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Monthly table — adapts based on whether comune is applied */}
            <div className="overflow-x-auto -mx-5 sm:mx-0">
              {current.comune_snapshot ? (
                <table className="w-full text-sm" data-testid="monthly-table-comune">
                  <thead>
                    <tr className="border-y border-zinc-200 bg-zinc-50">
                      <th className="px-3 py-2.5 text-left text-[10px] font-black uppercase tracking-wider text-zinc-600">Mese</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-black uppercase tracking-wider text-blue-700">Adulti × Notti</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-black uppercase tracking-wider text-emerald-700 hidden sm:table-cell">Esenti × Notti</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-black uppercase tracking-wider text-amber-700">Min. paganti × Notti</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-black uppercase tracking-wider text-zinc-900">Imposta</th>
                      <th className="px-3 py-2.5 text-center text-[10px] font-black uppercase tracking-wider text-zinc-400 hidden md:table-cell">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(current.monthly || {}).map(([key, v]) => (
                      <tr key={key} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`row-month-${key}`}>
                        <td className="px-3 py-3 font-bold text-zinc-900 capitalize">{monthNameIT(key)}</td>
                        <td className="px-3 py-3 text-right font-black text-blue-700 tabular-nums">{v.adult_nights}</td>
                        <td className="px-3 py-3 text-right text-emerald-700 tabular-nums hidden sm:table-cell">{v.exempt_minors_nights || 0}</td>
                        <td className="px-3 py-3 text-right text-amber-700 tabular-nums">{v.taxable_minors_nights || 0}</td>
                        <td className="px-3 py-3 text-right font-black text-zinc-900 tabular-nums">{fmtEur(v.tax_due || 0)}</td>
                        <td className="px-3 py-3 text-center hidden md:table-cell">
                          <button
                            onClick={() => downloadMonthPdf(key)}
                            disabled={downloadingMonth === key}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600 hover:text-zinc-900 disabled:opacity-50"
                            data-testid={`pdf-btn-${key}`}
                          >
                            {downloadingMonth === key ? <ArrowClockwise size={12} weight="bold" className="animate-spin" /> : <FilePdf size={12} weight="bold" />}
                            <span className="hidden lg:inline">PDF</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                      <td className="px-3 py-3 font-black text-zinc-900 uppercase text-xs tracking-wider">Totale</td>
                      <td className="px-3 py-3 text-right font-black text-blue-700 tabular-nums">
                        {Object.values(current.monthly || {}).reduce((s, v) => s + (v.adult_nights || 0), 0)}
                      </td>
                      <td className="px-3 py-3 text-right text-emerald-700 tabular-nums hidden sm:table-cell">
                        {Object.values(current.monthly || {}).reduce((s, v) => s + (v.exempt_minors_nights || 0), 0)}
                      </td>
                      <td className="px-3 py-3 text-right text-amber-700 tabular-nums">
                        {Object.values(current.monthly || {}).reduce((s, v) => s + (v.taxable_minors_nights || 0), 0)}
                      </td>
                      <td className="px-3 py-3 text-right font-black text-zinc-900 tabular-nums">
                        {fmtEur(Object.values(current.monthly || {}).reduce((s, v) => s + (v.tax_due || 0), 0))}
                      </td>
                      <td className="hidden md:table-cell" />
                    </tr>
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm" data-testid="monthly-table">
                  <thead>
                    <tr className="border-y border-zinc-200 bg-zinc-50">
                      <th className="px-3 py-2.5 text-left text-[10px] font-black uppercase tracking-wider text-zinc-600">Mese</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-black uppercase tracking-wider text-zinc-600">Adulti × Notti</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-black uppercase tracking-wider text-zinc-600">Bambini × Notti</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-black uppercase tracking-wider text-zinc-600 hidden sm:table-cell">Totale notti</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-black uppercase tracking-wider text-zinc-600 hidden md:table-cell">Prenotazioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(current.monthly || {}).map(([key, v]) => (
                      <tr key={key} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="px-3 py-3 font-bold text-zinc-900 capitalize">{monthNameIT(key)}</td>
                        <td className="px-3 py-3 text-right font-black text-blue-700 tabular-nums">{v.adult_nights}</td>
                        <td className="px-3 py-3 text-right font-black text-amber-700 tabular-nums">{v.children_nights || 0}</td>
                        <td className="px-3 py-3 text-right text-zinc-500 tabular-nums hidden sm:table-cell">{v.total_nights}</td>
                        <td className="px-3 py-3 text-right text-zinc-400 tabular-nums hidden md:table-cell">{v.reservations_count}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                      <td className="px-3 py-3 font-black text-zinc-900 uppercase text-xs tracking-wider">Totale</td>
                      <td className="px-3 py-3 text-right font-black text-blue-700 tabular-nums">
                        {Object.values(current.monthly || {}).reduce((s, v) => s + (v.adult_nights || 0), 0)}
                      </td>
                      <td className="px-3 py-3 text-right font-black text-amber-700 tabular-nums">
                        {Object.values(current.monthly || {}).reduce((s, v) => s + (v.children_nights || 0), 0)}
                      </td>
                      <td className="px-3 py-3 text-right font-black text-zinc-900 tabular-nums hidden sm:table-cell">
                        {Object.values(current.monthly || {}).reduce((s, v) => s + (v.total_nights || 0), 0)}
                      </td>
                      <td className="px-3 py-3 text-right text-zinc-500 tabular-nums hidden md:table-cell">
                        {Object.values(current.monthly || {}).reduce((s, v) => s + (v.reservations_count || 0), 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-4 bg-blue-50 border-l-4 border-blue-500 p-3 flex items-start gap-2">
              <Info size={14} weight="bold" className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-900 leading-relaxed">
                {current.comune_snapshot ? (
                  <>
                    Calcolo applicato per <strong>{current.comune_snapshot.name}</strong>: €{Number(current.comune_snapshot.tax_per_night).toFixed(2)}/notte/persona, max {current.comune_snapshot.max_nights || '∞'} notti per soggiorno, esenzione fino a {current.comune_snapshot.exemption_age >= 18 ? '17 anni (tutti i minori)' : `${current.comune_snapshot.exemption_age} anni`}. Scarica il PDF mese per mese per la rendicontazione al Comune.
                  </>
                ) : (
                  'Il sistema distribuisce le notti tra i mesi di soggiorno (es. 28/3→3/4 = 4 notti a marzo + 2 ad aprile). Seleziona un Comune qui sopra per ottenere il calcolo automatico dell\'imposta dovuta.'
                )}
              </p>
            </div>

            {/* Mobile-friendly per-month PDF buttons (visible only on small screens) */}
            {current.comune_snapshot && (
              <div className="md:hidden mt-4 grid grid-cols-2 gap-2" data-testid="mobile-pdf-buttons">
                {Object.keys(current.monthly || {}).map((key) => (
                  <button
                    key={key}
                    onClick={() => downloadMonthPdf(key)}
                    disabled={downloadingMonth === key}
                    className="flex items-center justify-center gap-1.5 border border-zinc-300 hover:border-zinc-900 px-3 py-2 text-[11px] font-bold capitalize text-zinc-700 disabled:opacity-50"
                    data-testid={`pdf-mobile-btn-${key}`}
                  >
                    {downloadingMonth === key ? <ArrowClockwise size={11} weight="bold" className="animate-spin" /> : <FilePdf size={11} weight="bold" />}
                    PDF {monthNameIT(key).split(' ')[0]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Manual entries list — visible only for manual reports */}
        {current && current.source_type === 'manual' && current.reservations?.length > 0 && (
          <div className="bg-white border border-zinc-200 p-5 sm:p-6 mb-6" data-testid="manual-entries-list">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-500">
                Inserimenti manuali · {current.reservations.length}
              </p>
              <Button
                onClick={() => setShowManualDialog(true)}
                variant="outline"
                className="rounded-none border-zinc-900 text-zinc-900 text-[10px] font-black uppercase tracking-wider h-7 px-3"
                data-testid="add-another-manual-btn"
              >
                <PlusIcon size={11} weight="bold" className="mr-1" /> Aggiungi
              </Button>
            </div>
            <div className="divide-y divide-zinc-100">
              {current.reservations.map((r, i) => {
                const totalAdultNights = (Number(r.adults) || 0) * (Number(r.nights) || 0);
                const totalTaxableNights = (Number(r.taxable_nights) || totalAdultNights);
                return (
                <div key={i} className="flex items-center justify-between py-2.5 gap-3" data-testid={`manual-row-${i}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-zinc-900 truncate">{r.guest_name || `Prenotazione #${i + 1}`}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {new Date(r.check_in).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} → {new Date(r.check_out).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {' · '}{r.nights} nott{r.nights === 1 ? 'e' : 'i'}
                      {' · '}{r.adults} adult{r.adults === 1 ? 'o' : 'i'}
                      {(r.children || 0) > 0 && ` · ${r.children} minor${r.children === 1 ? 'e' : 'i'}`}
                      {(r.children_exempt || 0) > 0 && ` (${r.children_exempt} esent${r.children_exempt === 1 ? 'e' : 'i'})`}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200" title={`${r.adults} adulti × ${r.nights} notti`}>
                        {r.adults}×{r.nights} = {totalAdultNights} persona-notti
                      </span>
                      {(r.children_taxable || 0) > 0 && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">
                          + {r.children_taxable} min. paganti × {r.nights} nt
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteManualEntry(i)}
                    className="text-zinc-400 hover:text-red-600 p-1.5 flex-shrink-0"
                    title="Elimina"
                    data-testid={`delete-manual-row-${i}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Previous reports */}
        {history.length > 0 && (
          <div className="bg-white border border-zinc-200 p-5 sm:p-6" data-testid="history-section">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-500 mb-3">Caricamenti precedenti</p>
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-zinc-900 truncate">{h.filename}</p>
                    <p className="text-[11px] text-zinc-400">
                      {new Date(h.uploaded_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })} · {h.reservations_count} prenotazioni · {h.source_type}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => loadReport(h.id)} className="text-[10px] font-black uppercase tracking-wider text-blue-700 hover:text-blue-900 px-2 py-1">Apri</button>
                    <button onClick={() => deleteReport(h.id)} className="text-zinc-400 hover:text-red-600 p-1" title="Elimina"><Trash size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {!loadingHistory && history.length === 0 && !current && (
          <div className="text-center text-xs text-zinc-400 mt-6">Nessun report ancora. Carica il tuo primo file.</div>
        )}
      </main>
    </div>
  );
}
